import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '../../../../common-utils/formatters';
import type { SoftwareType } from '../../../@types/Company';
import { SOFTWARE_CONFIGS } from '../../../@types/Company';
import type { Copro } from '../../../@types/Copro';
import type { RnicTableData } from '../../../@types/RnicTableData';
import { setSelectedCompany } from '../../../store/AuthReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { CoproRnicExcelExportEmptyError, exportCoprosRnicToExcel } from '../../../utils/coproRnicExcelExport';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { hasActiveSubscription } from '../../../utils/subscriptions';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';
import SoftwareExportUpload from './SoftwareExportUpload';

const keysNeeded = ['numeroDimmatriculation', 'nomDusageDeLaCopropriete', 'adresseDeReference', 'unmatchedValueBAN', 'banCodePostal', 'matchedBanNomCom', 'nombreTotalDeLots', 'nombreTotalDeLotsAUsageDhabitationDeBureauxOuDeComme', 'dateDeFinDuDernierMandat', 'identificationDuRepresentantLegalRaisonSocialeEtLeNume', 'matchedBanIdAdr'];
const finalNames = ['N° IMMAT', 'NOM', 'ADRESSE BRUTE RNIC', 'ADRESSE DÉTECTÉE', 'CP', 'COMMUNE', 'NB LOTS', 'MIXTE HAB / PRO', 'DATE FIN MANDAT', 'NOM DU SYNDIC DANS REGISTRE', 'matchedBanIdAdr', 'mandatUnformatted'];
const sortableColumns = {
  'N° IMMAT': 'string', NOM: 'string', 'ADRESSE BRUTE RNIC': 'string', 'ADRESSE DÉTECTÉE': 'string', CP: 'string', COMMUNE: 'string', 'NB LOTS': 'number', 'MIXTE HAB / PRO': 'number', 'DATE FIN MANDAT': 'date', 'NOM DU SYNDIC DANS REGISTRE': 'string',
};

const ActivitySetupStepCoproValidation: React.FC = () => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<RnicTableData[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState<string | boolean >(false);
  const [isCoproDataLoaded, setIsCoproDataLoaded] = useState(false);
  const [coproFetchFailed, setCoproFetchFailed] = useState(false);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const navigate = useNavigate();

  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const [isDeveloperView, setIsDeveloperView] = useState(false);
  const isDeveloper = useSelector((state: RootState) => state.auth.user?.roles?.includes('ROLE_DEVELOPER') || false);
  const [showMethodologyPicker, setShowMethodologyPicker] = useState(false);

  const [selectedSoftware, setSelectedSoftware] = useState<SoftwareType | null>(company?.selectedSoftware ?? null);
  const [savingSoftware, setSavingSoftware] = useState(false);
  const softwareSaved = company?.selectedSoftware !== null && company?.selectedSoftware !== undefined;
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [reportOverlayDismissed, setReportOverlayDismissed] = useState(false);
  const showReportOverlay = !!company?.importReportPdfFileName && !reportOverlayDismissed;

  useEffect(() => {
    const topObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTop(true);
        } else if (!entry.isIntersecting) {
          setIsTop(false);
        }
      },
      { threshold: 1 },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottom(true);
        } else if (!entry.isIntersecting) {
          setIsBottom(false);
        }
      },
      { threshold: 0 },
    );

    if (topRef.current) {
      topObserver.observe(topRef.current);
    }

    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      if (topRef.current) {
        topObserver.unobserve(topRef.current);
      }
      if (bottomRef.current) {
        bottomObserver.unobserve(bottomRef.current);
      }
    };
  }, [topRef, bottomRef]);

  useEffect(() => {
    if (company?.selectedSoftware) {
      setSelectedSoftware(company.selectedSoftware);
    }
  }, [company?.selectedSoftware]);

  const handleSaveSoftware = async() => {
    if (!selectedSoftware || !company || !jwt) {
      return;
    }
    setSavingSoftware(true);
    try {
      const response = await fetch(`/api/companies/${company.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({ selectedSoftware }),
      });
      if (!response.ok) {
        throw new Error('Failed to save software selection');
      }
      const updatedCompany = await response.json();
      dispatch(setSelectedCompany(updatedCompany));
      showToast('success', 'Logiciel enregistré avec succès.');
    } catch (err) {
      console.error('Error saving software :', err);
      showToast('error', 'Une erreur est survenue lors de l\'enregistrement du logiciel.');
    } finally {
      setSavingSoftware(false);
    }
  };

  const handleDownloadImportReport = async() => {
    if (!company?.id || !jwt) {
      return;
    }
    setDownloadingReport(true);
    try {
      const response = await fetch(`/api/companies/${company.id}/import-report-pdf`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_import_croissance_${company.name?.replace(/\s+/g, '_') || company.id}.pdf`;
      link.type = 'application/pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('success', 'Rapport téléchargé avec succès.');
    } catch (err) {
      console.error('Error downloading import report:', err);
      showToast('error', 'Une erreur est survenue lors du téléchargement du rapport.');
    } finally {
      setDownloadingReport(false);
    }
  };

  useEffect(() => {
    if (company && company.id && jwt) {
      fetch(`/api/companies/${company.id}/rnics`, JWTHeader(jwt))
        .then((response) => response.json())
        .then((response) => {
          interface RnicApiItem {
            [key: string]: string | number | boolean | null | undefined;
          }
          const formattedData = response.map((item: RnicApiItem) => {
            const newItem: Record<string, unknown> = {};
            keysNeeded.forEach((key, index) => {
              newItem[finalNames[index]] = item[key];
            });

            // format adresse
            if (!newItem['ADRESSE DÉTECTÉE'] && item.matchedBanNumero && item.matchedBanNomVoie) {
              newItem['ADRESSE DÉTECTÉE'] = `${item.matchedBanNumero}`;
              if (item.matchedBanRep) {
                newItem['ADRESSE DÉTECTÉE'] += ` ${item.matchedBanRep}`;
              }
              newItem['ADRESSE DÉTECTÉE'] += ` ${item.matchedBanNomVoie}`;
            }

            // try to parse DATE FIN MANDAT to formatDate
            if (newItem['DATE FIN MANDAT']) {
              newItem.mandatUnformatted = newItem['DATE FIN MANDAT'];
              newItem['DATE FIN MANDAT'] = formatDate(newItem['DATE FIN MANDAT'] as string);
            } else {
              newItem.mandatUnformatted = null;
              newItem['DATE FIN MANDAT'] = newItem['DATE FIN MANDAT'];
            }
            newItem.VALIDE = true;
            newItem.generalMeetingApiDate = item.detailsDonneesfinancieresDateag;
            return newItem as unknown as RnicTableData;
          });
          setData(formattedData);

          fetch(`/api/companies/${company.id}/copros?limit=100000&includeRefusedByAdmin=true`, JWTHeader(jwt))
            .then((responseCopro) => {
              if (!responseCopro.ok) {
                throw new Error('Failed to fetch existing copros');
              }
              return responseCopro.json();
            })
            .then((responseData) => {
              responseData = responseData.results;

              const initialSelectedIds = new Set<string>();
              const formattedDataCopro = formattedData.map((item: RnicTableData) => {
                const foundCopro = responseData.find((copro: Copro) => copro.remoteId === item['N° IMMAT']);
                if (foundCopro) {
                  item.VALIDE = foundCopro.validatedByUser;
                  item.validatedByUser = foundCopro.validatedByUser;
                  item.coproId = foundCopro.id;
                  item.generalMeetingDate = foundCopro.generalMeetingDate;
                  item.generalMeetingApiDate = foundCopro.generalMeetingApiDate;
                } else {
                  item.VALIDE = true;
                  item.validatedByUser = false;
                }
                if (item.VALIDE) {
                  initialSelectedIds.add(item['N° IMMAT']);
                }
                return item;
              });
              setData(formattedDataCopro);
              setSelectedIds(initialSelectedIds);
              setCoproFetchFailed(false);
              setIsCoproDataLoaded(true);
            })
            .catch((errorCopro) => {
              setCoproFetchFailed(true);
              setIsCoproDataLoaded(false);
              console.error('Error fetching copro data :', errorCopro);
              showToast('error', 'Une erreur est survenue lors du chargement des données. Enregistrement bloqué — rechargez la page.');
            });
        })
        .catch((errorRNIC) => {
          console.error('Error fetching data :', errorRNIC);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
        });
    }
  }, [company, jwt]);

  const rematchCoproIdsFromApi = async(): Promise<boolean> => {
    if (!company?.id || !jwt || !data) {
      return false;
    }
    try {
      const responseCopro = await fetch(
        `/api/companies/${company.id}/copros?limit=100000&includeRefusedByAdmin=true`,
        JWTHeader(jwt),
      );
      if (!responseCopro.ok) {
        throw new Error('Failed to rematch existing copros');
      }
      const responseData = await responseCopro.json();
      const results = responseData.results as Copro[];
      setData((prev) => {
        if (!prev) {
          return prev;
        }
        return prev.map((item): RnicTableData => {
          const foundCopro = results.find((copro) => copro.remoteId === item['N° IMMAT']);
          if (!foundCopro) {
            return item;
          }
          return {
            ...item,
            coproId: foundCopro.id,
            VALIDE: !!foundCopro.validatedByUser,
            validatedByUser: !!foundCopro.validatedByUser,
            generalMeetingDate: foundCopro.generalMeetingDate != null ? String(foundCopro.generalMeetingDate) : item.generalMeetingDate,
            generalMeetingApiDate: foundCopro.generalMeetingApiDate != null ? String(foundCopro.generalMeetingApiDate) : item.generalMeetingApiDate,
          };
        });
      });
      setCoproFetchFailed(false);
      setIsCoproDataLoaded(true);
      return true;
    } catch (errorRematch) {
      console.error('Error rematching copro data :', errorRematch);
      showToast('error', 'Impossible de resynchroniser les copropriétés après enregistrement.');
      return false;
    }
  };

  const handleCheckedDataChange = (checked: boolean, id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSave = async({ navigateAfter = true }: { navigateAfter?: boolean } = {}) => {
    if (loading) {
      return false;
    }
    if (!isCoproDataLoaded || coproFetchFailed) {
      showToast('error', 'Veuillez attendre que les données soient chargées avant d\'enregistrer.');
      return false;
    }

    setLoading(true);

    if (!company || !jwt || !company.id || !data) {
      setLoading(false);
      setError('Informations obligatoires manquantes.');
      return false;
    }

    const createCoprosBatch = async(copros: Record<string, unknown>[]) => {
      try {
        const response = await fetch(`/api/companies/${company.id}/copros/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: JWTAuthorization(jwt),
          },
          body: JSON.stringify(copros),
        });

        if (!response.ok) {
          throw new Error('Error creating copros batch');
        }

        return await response.json();
      } catch (errorApi) {
        console.error('Error creating copros batch :', errorApi);
        showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
      }
      return null;
    };

    const updateCopro = async(coproId: number, payload: Record<string, unknown>) => {
      try {
        const response = await fetch(`/api/copros/${coproId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: JWTAuthorization(jwt),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('Error updating copro');
        }
      } catch (errorApi) {
        console.error('Error updating copro :', errorApi);
        showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
      }
    };

    const processCoproData = async() => {
      const coprosToBatch = [];
      const coprosToUpdate = [];

      data.forEach((copro) => {
        const isSelected = selectedIds.has(copro['N° IMMAT']);
        const payload = {
          name: copro.NOM,
          batchSize: copro['MIXTE HAB / PRO'],
          type: 1,
          remoteId: copro['N° IMMAT'],
          termEndAt: copro.mandatUnformatted ? moment(copro.mandatUnformatted).format('YYYY-MM-DD') : '',
          validatedByUser: isSelected,
          generalMeetingApiDate: copro.generalMeetingApiDate ? moment(copro.generalMeetingApiDate).format('YYYY-MM-DD') : '',
          addresses: copro['ADRESSE DÉTECTÉE'] && copro.CP && copro.COMMUNE && copro.matchedBanIdAdr
            ? [{
              postalCode: copro.CP,
              address: copro['ADRESSE DÉTECTÉE'],
              city: copro.COMMUNE,
              remoteId: copro.matchedBanIdAdr,
            }]
            : [],
        };

        if (!copro.coproId) {
          // Only create newly selected RNIC rows — never bulk-create unchecked ones
          if (isSelected) {
            coprosToBatch.push(payload);
          }
        } else if (isSelected !== copro.validatedByUser) {
          const updatePayload = { ...payload, generalMeetingDate: copro.generalMeetingDate };
          delete updatePayload.addresses;
          coprosToUpdate.push({ id: copro.coproId, payload: updatePayload });
        }
      });


      if (coprosToBatch.length > 0) {
        await createCoprosBatch(coprosToBatch);
      }

      await Promise.all(coprosToUpdate.map(({ id, payload }) => updateCopro(id, payload)));
    };

    try {
      await processCoproData();
      await rematchCoproIdsFromApi();
      await updateSetupState(company, jwt, 'coproValidation', dispatch);

      setLoading(false);
      if (navigateAfter) {
        navigate(ROUTES.ANALYZING_ADD_COPROPRIETES);
      }
      return true;
    } catch (errorSaving) {
      setLoading(false);
      showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
      console.error('Error saving data :', errorSaving);
      return false;
    }
  };

  const headers = data && data.length > 0 ? Object.keys(data[0]) : [];
  const headersToRemove = [
    'generalMeetingApiDate',
    'generalMeetingDate',
    'matchedBanIdAdr',
    'VALIDE',
    'coproId',
    'validatedByUser',
    'mandatUnformatted',
  ];

  if (!isDeveloperView) {
    headersToRemove.push('ADRESSE DÉTECTÉE', 'CP', 'COMMUNE');
  }

  for (const header of headersToRemove) {
    const index = headers.indexOf(header);
    if (index >= 0) {
      headers.splice(index, 1);
    }
  }

  const handleExport = async({ navigateAfter = true }: { navigateAfter?: boolean } = {}) => {
    if (!isCoproDataLoaded || coproFetchFailed) {
      showToast('error', 'Veuillez attendre que les données soient chargées.');
      return;
    }
    if (!company || !company.id || !jwt) {
      return;
    }
    setExportLoading(true);
    const saved = await handleSave({ navigateAfter: false });
    if (!saved) {
      setExportLoading(false);
      return;
    }
    try {
      await exportCoprosRnicToExcel(company.id, company.siren ?? '', jwt);
      showToast('success', 'Export Excel réussi.');
    } catch (errorExport) {
      if (errorExport instanceof CoproRnicExcelExportEmptyError) {
        showToast('warning', errorExport.message);
      } else {
        console.error('Error exporting xlsx :', errorExport);
        showToast('error', 'Une erreur est survenue lors de la génération du fichier.');
      }
    } finally {
      setExportLoading(false);
    }
    if (navigateAfter) {
      navigate(ROUTES.ANALYZING_ADD_COPROPRIETES);
    }
  };

  const toggleView = () => {
    setIsDeveloperView(!isDeveloperView);
  };

  const isInactiveCompany = !hasActiveSubscription(company);
  const saveDisabled = loading || !isCoproDataLoaded || coproFetchFailed;

  const renderSaveButton = () => (
    <div
      className={classNames(
        'text-right w-full md:w-auto mt-10 flex flex-col md:flex-row gap-4 justify-end',
        !isTop && !isBottom && 'fixed z-[1000] right-0 bottom-0 md:right-5 md:bottom-5 bg-white rounded',
      )}
    >
      <button
        type="button"
        disabled={saveDisabled}
        onClick={async() => {
          const saved = await handleSave();
          if (saved) {
            navigate(ROUTES.ANALYZING_MARKET_VISION);
          }
        }}
        className={classNames(
          'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
          saveDisabled && 'opacity-80 cursor-not-allowed',
        )}
      >
        {loading && (
          <>
            <Spinner aria-label="Center-aligned spinner" size="sm" />
            <span className="ml-2">Enregistrement</span>
          </>
        )}
        {!loading && 'Enregistrer & Voir mon parc'}
      </button>
      {!isInactiveCompany && (
        <button
          type="button"
          disabled={saveDisabled}
          onClick={() => handleSave()}
          className={classNames(
            'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
            saveDisabled && 'opacity-80 cursor-not-allowed',
          )}
        >
          {loading && (
            <>
              <Spinner aria-label="Center-aligned spinner" size="sm" />
              <span className="ml-2">Enregistrement</span>
            </>
          )}
          {!loading && 'Prochaine étape'}
        </button>
      )}
    </div>
  );

  return (
    <SetupPage
      title="VALIDATION DE VOS COPROPRIÉTÉS ENREGISTRÉES DANS LE REGISTRE DES COPROS ET AJOUT DES ASL ET DES IMMEUBLES TERTIAIRES SI VOUS EN AVEZ"
      step={'coproValidation'}
      children={(
        <>
          <div ref={topRef} style={{ height: '1px' }} />


          {/* Methodology 1 + Upload – wrapped for overlay positioning */}
          <div className="relative">
            {/* Methodology 1 - Software Selection */}
            <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h2 className="mb-2 flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
                    <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">MÉTHODOLOGIE 1 :</span>
                    <span>RÉIMPORTER LES COPROS TRAITÉES OU IMPORTER LES DEPUIS VOTRE LOGICIEL MÉTIER</span>
                  </h2>
                  <p className="mb-3 text-sm text-blue-800">
                    Sélectionnez votre logiciel métier puis cliquez sur Enregistrer.
                  </p>
                  <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {SOFTWARE_CONFIGS.map((config) => {
                      const isSelected = selectedSoftware === config.id;
                      return (
                        <button
                          key={config.id}
                          onClick={() => setSelectedSoftware(config.id)}
                          className={classNames(
                            'relative px-3 py-3 rounded-lg transition-all font-semibold text-sm text-left shadow-sm border-2 flex items-center justify-between',
                            isSelected
                              ? 'bg-rgpd-blue-light text-white border-rgpd-blue-light'
                              : config.implemented
                                ? 'bg-white text-blue-900 border-blue-300 hover:bg-blue-50 hover:border-blue-400'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                          )}
                        >
                          <span>{config.displayName}</span>
                          {isSelected && <CheckCircleIcon className="h-5 w-5 text-white" />}
                          {!config.implemented && !isSelected && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Bientôt</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveSoftware}
                      disabled={!selectedSoftware || savingSoftware || selectedSoftware === company?.selectedSoftware}
                      className={classNames(
                        'px-6 py-2 rounded-lg font-semibold text-white transition-all',
                        selectedSoftware && selectedSoftware !== company?.selectedSoftware
                          ? 'bg-rgpd-green hover:bg-rgpd-green-dark cursor-pointer'
                          : 'bg-gray-300 cursor-not-allowed',
                      )}
                    >
                      {savingSoftware ? <Spinner size="sm" /> : 'Enregistrer le type d\'import'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Show upload block only after software is saved */}
            {softwareSaved && company && (
              <SoftwareExportUpload
                company={company}
                jwt={jwt}
                onUploadSuccess={(updatedCompany) => dispatch(setSelectedCompany(updatedCompany))}
                onExport={() => handleExport({ navigateAfter: false })}
                exporting={exportLoading}
              />
            )}

            {/* Import report overlay – covers Méthodologie 1 + upload section */}
            {showReportOverlay && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-gray-100/70 backdrop-blur-md">
                <DocumentTextIcon className="mb-3 h-10 w-10 text-rgpd-blue-light" />
                <h3 className="mb-2 text-lg font-bold text-gray-900">Rapport d'import disponible</h3>
                <p className="mb-1 max-w-sm text-center text-sm text-gray-600">
                  Un rapport de synthèse de l'import de vos copropriétés est disponible.
                </p>
                {company?.importReportPdfUploadedAt && (
                  <p className="mb-4 text-xs text-gray-400">
                    Généré le {formatDate(company.importReportPdfUploadedAt)}
                  </p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => setReportOverlayDismissed(true)}
                    className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50"
                  >
                    Fermer
                  </button>
                  <button
                    onClick={handleDownloadImportReport}
                    disabled={downloadingReport}
                    className="flex items-center gap-2 rounded-lg bg-rgpd-blue-light px-5 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-110 disabled:opacity-60"
                  >
                    {downloadingReport ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
                    Télécharger le rapport d'import
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
             <h2 className="flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
                <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">MÉTHODOLOGIE 2 :</span>
                <span>DÉSÉLECTIONNER LES COPROPRIÉTÉS QUE VOUS NE GÉREZ PLUS PUIS CLIQUER SUR ENREGISTRER</span>
              </h2>
          </div>

          <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col items-start justify-between gap-8 lg:flex-row">
              <p className="flex-1 text-lg leading-relaxed font-medium text-gray-800">
                À CE JOUR
                <span className="mx-1 font-bold text-rgpd-blue-light">
                  {data && data.length}
                  {!data && <Skeleton height={15} width={30} inline />}
                  {' '}
                  COPROPRIÉTÉS HORS ASL, AFUL ET IMMEUBLES TERTIAIRES
                </span>
                SONT RATTACHÉES À VOTRE NOM DANS LE REGISTRE DES COPROPRIÉTÉS. VOUS POURREZ RAJOUTER LES COPROPRIÉTÉS MANQUANTES DANS L'ÉTAPE 2.
              </p>

              <div className="flex w-full shrink-0 flex-col items-end gap-3 lg:w-auto">
                {isDeveloper && (
                  <button
                    onClick={toggleView}
                    className="mb-2 flex w-full items-center justify-center rounded border border-rgpd-blue-dark px-4 py-2 text-rgpd-blue-dark transition-colors hover:bg-rgpd-blue-dark hover:text-white lg:w-auto"
                  >
                    {isDeveloperView ? (
                      <>
                        <EyeSlashIcon className="mr-2 h-5 w-5" />
                        Vue utilisateur
                      </>
                    ) : (
                      <>
                        <EyeIcon className="mr-2 h-5 w-5" />
                        Vue développeur
                      </>
                    )}
                  </button>
                )}
                <button
                  className="hover:bg-opacity-90 w-full rounded-lg bg-rgpd-blue-light px-6 py-3 text-center font-semibold tracking-wide text-white shadow-md transition-all lg:w-auto"
                  onClick={() => handleExport()}
                >
                  {exportLoading && <Spinner aria-label="Center-aligned spinner" size="lg" className="mx-8 my-1" />}
                  {!exportLoading && (
                    <div className="flex flex-col items-center leading-tight">
                      <span>Enregistrer</span>
                      <span className="my-0.5 text-sm">&</span>
                      <span>Exporter en Excel</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Methodology Coming Soon Popup */}
          <Popup
            isOpen={showMethodologyPicker}
            onClose={() => setShowMethodologyPicker(false)}
            children={(
              <div className="max-w-md p-6">
                <h3 className="mb-4 text-xl font-bold text-gray-900">
                  Méthodologie en cours de confection
                </h3>
                <p className="mb-4 text-gray-700">
                  La méthodologie est en cours de confection.
                  <br />
                  Merci de nous contacter par email pour plus d'informations et d'aide à la mise en place et l'export.
                </p>
                <button
                  onClick={() => setShowMethodologyPicker(false)}
                  className="hover:bg-opacity-90 w-full rounded-lg bg-rgpd-blue-light px-6 py-3 font-semibold text-white transition-all"
                >
                  Fermer
                </button>
              </div>
            )}
          />

          {(!data || !isCoproDataLoaded) && (
            <p className="mx-10 my-32 text-center">
              <Spinner aria-label="Center-aligned spinner" size="xl" />
            </p>
          )}
          {data && data.length > 0 && isCoproDataLoaded && (
            <>
              <div className="mb-4 flex items-center justify-between px-2">
                <span className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 font-bold text-rgpd-blue-dark">
                  {selectedIds.size} sélectionné(s)
                </span>
              </div>
              <Table
                isSearchable
                sortableColumns={sortableColumns}
                data={data.map((item) => ({
                  ...item,
                  VALIDE: selectedIds.has(item['N° IMMAT']),
                }))}
                headers={headers}
                columnChecking
                tableClass="text-sm"
                idKey="N° IMMAT"
                dataChangeCallback={handleCheckedDataChange}
                stickyHeader
              />
            </>
          )}
          {data && data.length === 0 && isCoproDataLoaded && (
            <div className="mt-5 flex w-full place-content-center">
              <Badge
                mode="error"
                text="Aucune copropriété trouvée. Nous vous suggérons de vérifier que vos copropriétés sont bien présentes dans le registre national des copropriétés."
              />
            </div>
          )}
          {/* must be on top of the button otherwise the button appearance makes it dissapear */}
          <div ref={bottomRef} style={{ height: '1px' }} />
          {isCoproDataLoaded && renderSaveButton()}
          <Popup
            isOpen={error != false}
            children={(
              <Badge
                mode="error"
                text={typeof error === 'string' ? error : 'Une erreur inconnue est survenue'}
              />
            )}
            onClose={() => setError(false)}
          />
        </>
      )}
    />
  );
};

export default ActivitySetupStepCoproValidation;